import SelectComponent from "@via/components/select/select.tsx";
import { createRunEnvironmentOptions } from "@via/utils/form-validators.tsx";
import { getDocsUrl } from "@via/utils/config.tsx";
import { AGENT_LIMITATIONS_DOCS_PATH } from "@via/utils/constants.tsx";
import {
  type ProfileClusterForClient,
  type RunEnvironmentTransport,
} from "common";
import { type JSX } from "react";
import { type FieldErrors, type UseFormRegister } from "react-hook-form";

export const basicsCardSummary = (params: { name?: string | null }): string =>
  params.name?.trim() || "Not named yet";

export function BasicsCard({
  register,
  errors,
  publicRunEnvironments,
  onTemplateChange,
  onTransportUserChange,
  isExisting,
  sshOnly,
  transport,
}: {
  register: UseFormRegister<ProfileClusterForClient>;
  errors: FieldErrors<ProfileClusterForClient>;
  publicRunEnvironments?: ProfileClusterForClient[];
  onTemplateChange: (option: any) => void;
  /**
   * Fired only when the user picks a transport here. Hydrating a saved row
   * writes the same field through the form's reset(), which raises no change
   * event, so this is what lets the form reveal the Agent card on a choice
   * without popping it open every time an environment is loaded.
   */
  onTransportUserChange: () => void;
  // The server already refuses to change transport on an existing
  // environment, so this only disables the control as a hint of that rule.
  // It is not itself the guarantee.
  isExisting: boolean;
  /**
   * Set where the surrounding surface can only use an ssh environment, which
   * today means the SSH data connection drawer. Offering Agent there is a
   * choice that surface cannot honor: the environment saves correctly and is
   * then filtered straight back out of the picker the drawer exists to fill.
   *
   * ⚠️ This hides the control, and the parent form uses shouldUnregister:
   * true, so the field is then never registered and drops out of the submitted
   * values. The payload pins ssh itself rather than relying on this input.
   * Issue #472.
   */
  sshOnly: boolean;
  /**
   * The transport currently chosen in the form, watched by the parent. While Agent is chosen on an
   * environment that is not saved yet, the card says what an agent environment needs (agent-4-2).
   */
  transport?: RunEnvironmentTransport;
}): JSX.Element {
  return (
    <>
      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Name <span className="text-red-400">*</span>
        </label>
        <p className="text-xs text-gray-500">
          This is what appears in the run environment picker when you launch.
        </p>
        <div className="mt-2">
          <input
            // The shared options say "Profile name is required", and they are
            // shared with the organization form, which still calls the field
            // Profile Name. Override the message here only, so this form names
            // the field the user can actually see.
            {...register("name", {
              ...createRunEnvironmentOptions.name,
              required: "Name is required",
            })}
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="e.g. Lab cluster"
          />
          {errors?.name ? (
            <span className="text-xs text-rose-500">{errors.name.message}</span>
          ) : null}
        </div>
      </div>

      {sshOnly ? null : (
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900">
            How Foundry Connect reaches this cluster
          </label>
          <p className="text-xs text-gray-500">
            SSH connects out to a login node with a key. Agent runs a small
            process in your own account on the cluster, which dials back here,
            so it works where inbound SSH is blocked. This cannot be changed
            later.
          </p>
          <div className="mt-2 flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                value="ssh"
                disabled={isExisting}
                {...register("transport", { onChange: onTransportUserChange })}
              />{" "}
              SSH
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                value="agent"
                disabled={isExisting}
                {...register("transport", { onChange: onTransportUserChange })}
              />{" "}
              Agent
            </label>
          </div>
          {"agent" === transport && !isExisting ? (
            <p
              data-testid="agent-transport-limits"
              className="mt-2 text-xs text-amber-700">
              An agent environment needs a connected agent of a recent version,
              and a write folder on the agent for uploads and writable data
              sources.{" "}
              <a
                href={getDocsUrl(AGENT_LIMITATIONS_DOCS_PATH)}
                target="_blank"
                rel="noreferrer"
                className="underline">
                What agent environments do differently
              </a>
            </p>
          ) : null}
        </div>
      )}

      {publicRunEnvironments?.length ? (
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Template
          </label>
          <div className="mt-2" data-testid="public-template">
            <SelectComponent
              onChange={onTemplateChange}
              name="PublicTemplate"
              options={
                publicRunEnvironments?.map(({ name, id }) => ({
                  label: name,
                  value: id,
                })) ?? []
              }
              enableClear={true}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
